import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';

import { Box, Grid, Button } from '@material-ui/core';
import { useStyles } from './styles';
import download from '../../assets/icons/download.png';
import { ImUndo2 } from "react-icons/im";
import Card from '@mui/material/Card';
import LoadingEvents from '../../components/LoadingEvents';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import data_results_logo from '../../assets/images/data_results_logo.png';
import ArrowLeft from '../../assets/icons/arrowLeft.png';

import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';

import ReactDOM from 'react-dom';
import JSZip from 'jszip';
import { PDFDocument } from 'pdf-lib';

/* ===========================
   Constantes/Helpers globais
   =========================== */

// Tamanho A4 em mm e margens
const PDF_MM = { W: 210, H: 297, MARGIN: 1 };

// Parâmetros de captura (bons para nitidez x performance)
const CAPTURE = { SCALE: 1.35, DPI: 180, JPEG_Q: 0.86 };
// Concorrência de capturas (2 é seguro, 3 agressivo)
const CAPTURE_CONCURRENCY = 2;

const NF_PTBR = new Intl.NumberFormat('pt-BR');

/** Dorme em ms (útil para yields cooperativos) */
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

/** Micro yield para liberar o main thread e manter a UI responsiva */
const microYield = () => new Promise((res) => setTimeout(res, 0));

/** Throttle de setState: 1 atualização por frame */
let _raf = 0;
const setProgressRaf = (setter) => (value) => {
    if (_raf) return;
    _raf = requestAnimationFrame(() => {
        setter(value);
        _raf = 0;
    });
};

/** Aguarda um elemento aparecer via MutationObserver (evita busy-wait) */
function waitForElementById(id, timeoutMs = 15000) {
    return new Promise((resolve, reject) => {
        const found = document.getElementById(id);
        if (found) return resolve(found);

        const obs = new MutationObserver(() => {
            const el = document.getElementById(id);
            if (el) {
                try { clearTimeout(to); } catch { }
                obs.disconnect();
                resolve(el);
            }
        });

        obs.observe(document.documentElement, { childList: true, subtree: true });

        const to = setTimeout(() => {
            try { obs.disconnect(); } catch { }
            try { clearTimeout(to); } catch { }
            reject(new Error(`Elemento #${id} não encontrado em ${timeoutMs}ms`));
        }, timeoutMs);
    });
}

/**
 * Executa tarefas assíncronas com concorrência limitada.
 * Mantém ordem dos resultados, oferece callback de progresso.
 */
async function concurrentMap(items, limit, mapper, onProgress) {
    const total = items.length;
    const results = new Array(total);
    let nextIndex = 0;
    let done = 0;

    const worker = async () => {
        while (true) {
            const idx = nextIndex++;
            if (idx >= total) break;
            results[idx] = await mapper(items[idx], idx);
            done++;
            onProgress && onProgress(done, total);
            if (done % 3 === 0) await microYield();
        }
    };

    const workers = new Array(Math.min(limit, total)).fill(0).map(worker);
    await Promise.all(workers);
    return results;
}

/** Converte canvas em dataURL JPEG e limpa o canvas para ajudar GC */
function canvasToJpegDataURL(canvas, quality = CAPTURE.JPEG_Q) {
    const url = canvas.toDataURL('image/jpeg', quality);
    try {
        const ctx = canvas.getContext('2d');
        ctx && ctx.clearRect(0, 0, canvas.width, canvas.height);
    } catch { }
    return url;
}

/** Pré-decodifica imagens dentro de um container específico */
async function decodeImagesIn(container) {
    const imgs = Array.from(container.querySelectorAll('img'));
    await Promise.all(
        imgs.map((img) => (img.decode ? img.decode().catch(() => { }) : Promise.resolve()))
    );
}

/* ===========================
   Componente
   =========================== */

const RecomendationReport = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const styles = useStyles();

    const { data } = useSelector(state => state.recomendationReportData);

    // Estados de UI
    const [isLoadingEvents, setIsLoadingEvents] = useState(false);
    const [loading, _setLoading] = useState(false);
    const [progress, _setProgress] = useState(0);
    const [message, _setMessage] = useState('');

    // Throttle para progresso
    const setProgressSmooth = useRef(setProgressRaf(_setProgress)).current;

    // Evitar setState após unmount
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);
    const safeSet = (fn) => (...args) => { if (mountedRef.current) fn(...args); };
    const setLoading = safeSet(_setLoading);
    const setMessage = safeSet(_setMessage);
    const setProgress = safeSet(setProgressSmooth);

    // Flags e dados (refs evitam re-renders desnecessários)
    const startRef = useRef(true);
    const reportTemplateRef = useRef(undefined);
    const combinedListRef = useRef(undefined);
    const isDownloadReportRef = useRef(undefined);
    const analyseSampleIDsRef = useRef(undefined);

    // Carrega dados do Redux + localStorage sem alterar visual
    useEffect(() => {
        if (data) {
            reportTemplateRef.current = data.reportTemplate;
            combinedListRef.current = data.combinedList;
            isDownloadReportRef.current = data.isDownloadReport;
            analyseSampleIDsRef.current = data.analyseSampleIDs;
        }

        const storedData = localStorage.getItem('recomendationReportData');
        if (storedData) {
            const parsed = JSON.parse(storedData);
            if (parsed.reportTemplate !== undefined) reportTemplateRef.current = parsed.reportTemplate;
            if (parsed.combinedList !== undefined) combinedListRef.current = parsed.combinedList;
            if (parsed.isDownloadReport !== undefined) isDownloadReportRef.current = parsed.isDownloadReport;
            if (parsed.analyseSampleIDs !== undefined) analyseSampleIDsRef.current = parsed.analyseSampleIDs;
        }

        if (isDownloadReportRef.current && combinedListRef.current) {
            generateAndDownloadPdfSeveral();
        } else if (isDownloadReportRef.current && !combinedListRef.current) {
            setMessage('1/4 - Preparando relatório(s), aguarde...');
            setLoading(true);
            setProgress(0);
            consultAnalyseSampleParameterTemplate();
        }

        if (startRef.current) {
            startRef.current = false;
            dispatch({
                type: 'SET_RECOMENDATION_REPORT_DATA',
                payload: {
                    combinedList: combinedListRef.current,
                    reportTemplate: reportTemplateRef.current,
                    isDownloadReport: isDownloadReportRef.current,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    /* ===========================
       Fase 1: Montagem do combinedList
       =========================== */

    const consultAnalyseSampleParameterTemplate = async () => {
        const analyseIds = analyseSampleIDsRef.current || [];
        if (!analyseIds.length) {
            setLoading(false);
            return;
        }

        const totalRequests = analyseIds.length;
        const combinedListAux = [];

        const sampleConcurrency = 5;  // ok para rede

        const mapOneTemplate = async (analyseSampleID, parameterTemplate) => {
            const paramTemplateID = parameterTemplate.parameterTemplateID;
            const [resultInterpretation, resultObs] = await Promise.all([
                api.get(`AnalyseSample/ConsultAnalyseSampleResultInterpretation?id=${analyseSampleID}&&idAux=${paramTemplateID}`),
                api.get(`Interpretation/Recommendation/LoadAnalyseSampleObservationList?analyseSampleID=${analyseSampleID}&&idAux=${paramTemplateID}&&isForReport=false`)
                    .catch(() => ({ data: { response: [] } })),
            ]);
            if (!resultInterpretation?.data?.response) return null;

            const obsList = resultObs?.data?.response || [];
            return {
                analyseSampleParameterTemplates: parameterTemplate,
                agronomist: parameterTemplate.agronomist,
                lstAnalyseSampleResultInterpretation: resultInterpretation.data.response.item1,
                lstParameterTemplateItem: resultInterpretation.data.response.item2,
                observationList: obsList.length > 0 ? obsList : undefined,
            };
        };

        const mapOneSample = async (analyseSampleID) => {
            try {
                const resultAnalyseSample = await api.get(`AnalyseSample/GetById?id=${analyseSampleID}`);
                if (!resultAnalyseSample?.data?.response) return null;
                const analyseSampleReturn = resultAnalyseSample.data.response;

                const resultAnalyseSampleParameterTemplate = await api.get(
                    `AnalyseSample/ConsultAnalyseSampleParameterTemplate?id=${analyseSampleID}`
                );
                if (!resultAnalyseSampleParameterTemplate?.data?.response) return null;

                const parameterTemplates = resultAnalyseSampleParameterTemplate.data.response || [];
                const templateConcurrency = 6;

                const perTemplate = await concurrentMap(
                    parameterTemplates,
                    templateConcurrency,
                    (parameterTemplate) => mapOneTemplate(analyseSampleID, parameterTemplate)
                );

                perTemplate.filter(Boolean).forEach((payload) => {
                    combinedListAux.push({ ...payload, analyseSampleReturn });
                });
                return true;
            } catch (error) {
                console.log('Error generating consultAnalyseSampleParameterTemplate:', error);
                return null;
            }
        };

        await concurrentMap(
            analyseIds,
            sampleConcurrency,
            mapOneSample,
            (done) => setProgress(Math.round((done / totalRequests) * 100))
        );

        setLoading(false);
        dispatch({
            type: 'SET_RECOMENDATION_REPORT_DATA',
            payload: {
                combinedList: combinedListAux,
                reportTemplate: reportTemplateRef.current,
                isDownloadReport: isDownloadReportRef.current,
            },
        });

        combinedListRef.current = combinedListAux;

        if (isDownloadReportRef.current && combinedListAux?.length) {
            await microYield();
            generateAndDownloadPdfSeveral();
        }
    };

    /* ===========================
       Fase 2/3: Geração de PDFs
       =========================== */

    // Função de captura → bytes PDF (1 item)
    const captureElementToPdfBytes = async (pdfDataHtml) => {
        // aguarda fontes
        await document.fonts.ready;

        // captura com dimensões fixas pra nitidez
        const canvas = await html2canvas(pdfDataHtml, {
            scale: CAPTURE.SCALE,
            dpi: CAPTURE.DPI,
            useCORS: true,
            logging: false,
            backgroundColor: '#fff',
            // mantém a largura exata do elemento (evita blur)
            width: pdfDataHtml.scrollWidth,
            windowWidth: pdfDataHtml.scrollWidth,
            removeContainer: true,
            ignoreElements: (el) => el?.hasAttribute?.('data-skip-capture')
        });

        const imgData = canvasToJpegDataURL(canvas, CAPTURE.JPEG_Q);

        const imgWidthInMM = PDF_MM.W - 2 * PDF_MM.MARGIN;
        const imgHeightInMM = (imgWidthInMM / canvas.width) * canvas.height;
        const totalPages = Math.ceil(imgHeightInMM / PDF_MM.H);

        const doc = new jsPDF('p', 'mm', 'a4', true); // compress interno habilitado
        for (let page = 1; page <= totalPages; page++) {
            if (page !== 1) doc.addPage();
            const offsetY = -(page - 1) * PDF_MM.H;
            doc.addImage(
                imgData,
                'JPEG',
                PDF_MM.MARGIN,
                offsetY + PDF_MM.MARGIN,
                imgWidthInMM,
                imgHeightInMM,
                undefined,
                'FAST'
            );
        }
        return doc.output('arraybuffer'); // já comprimido
    };

    const generateAndDownloadPdfSeveral = async () => {
        try {
            const combinedList = combinedListRef.current || [];
            if (!combinedList.length) return;

            // 2/4 – Monta DOM off-screen uma vez
            const container = document.createElement('div');
            container.style.position = 'absolute';
            container.style.left = '-9999px';
            container.style.top = '0';
            container.style.width = '0';
            container.style.height = '0';
            document.body.appendChild(container);

            const pdfContentContainer = document.createElement('div');
            container.appendChild(pdfContentContainer);

            setMessage('2/4 - Carregando Arquivos, aguarde...');
            setLoading(true);

            ReactDOM.render(
                <div style={{ overflowX: 'hidden' }}>
                    <PdfContent />
                </div>,
                pdfContentContainer
            );

            // garante montagem
            await microYield();
            setProgress(0);

            // Pré-carrega fontes + imagens do bloco (1 única vez)
            await document.fonts.ready;
            await decodeImagesIn(pdfContentContainer);

            // Captura concorrente (2 por vez)
            const total = combinedList.length;
            const allPdfBytes = new Array(total);

            await concurrentMap(
                combinedList,
                CAPTURE_CONCURRENCY,
                async (_, i) => {
                    const id = 'pdfData' +
                        combinedList[i].analyseSampleReturn.analyseSampleID +
                        combinedList[i].analyseSampleParameterTemplates.parameterTemplateID;

                    const pdfDataHtml = await waitForElementById(id);

                    const bytes = await captureElementToPdfBytes(pdfDataHtml);
                    allPdfBytes[i] = {
                        pdfBytes: bytes,
                        analyseSampleID: combinedList[i].analyseSampleReturn.analyseSampleID,
                    };
                    return true;
                },
                (done) => setProgress(Math.round((done / total) * 100))
            );

            // desmonta o container off-screen
            ReactDOM.unmountComponentAtNode(pdfContentContainer);
            document.body.removeChild(container);

            setMessage('3/4 - Efetuando Download, aguarde...');
            setLoading(true);

            // Envia bytes ao backend (zipado), sem compressão redundante
            await sendPdfBytesToBackend(allPdfBytes);

            localStorage.setItem('secondScreenClosed', 'true');
            // window.close();
        } catch (error) {
            console.log('Error generating PDF:', error);
        }
    };

    const generateAndDownloadPdf = async () => {
        try {
            const combinedList = combinedListRef.current || [];
            if (!combinedList.length) return;

            // Gera DOM inline (já está na tela quando não isDownloadReport)
            const total = combinedList.length;
            const allPdfBytes = [];

            // Pré-fontes (uma vez)
            await document.fonts.ready;

            for (let i = 0; i < total; i++) {
                const id = "pdfData" +
                    combinedList[i].analyseSampleReturn.analyseSampleID +
                    combinedList[i].analyseSampleParameterTemplates.parameterTemplateID;

                const pdfDataHtml = await waitForElementById(id);
                // Pré-decodifica imagens desse bloco
                await decodeImagesIn(pdfDataHtml);

                const bytes = await captureElementToPdfBytes(pdfDataHtml);
                allPdfBytes.push({
                    pdfBytes: bytes,
                    analyseSampleID: combinedList[i].analyseSampleReturn.analyseSampleID,
                });

                if ((i + 1) % 2 === 0) await microYield();
            }

            setMessage('Gerando relatório(s), aguarde...');
            setLoading(true);

            // Envia sem compressão extra (já vem comprimido do jsPDF + zip)
            await sendPdfBytesToBackend(allPdfBytes, true);

            await sleep(1000);
            window.close();
        } catch (error) {
            console.log('Error generating PDF:', error);
        }
    };

    // Mantive para compatibilidade, mas não uso no hot path
    const applyPdfCompression = async (pdfBytes) => {
        try {
            const pdfDoc = await PDFDocument.load(pdfBytes);
            await pdfDoc.compress();
            const compressedBytes = await pdfDoc.save();
            return compressedBytes;
        } catch (error) {
            console.error('Erro ao aplicar compressão ao PDF:', error);
            return pdfBytes;
        }
    };

    /* ===========================
       Fase 4: Envio ao backend (zip)
       =========================== */

    const sendPdfBytesToBackend = async (allPdfBytes, isSingle = false) => {
        const zip = new JSZip();
        const formData = new FormData();
        const analyseSampleIds = [];

        for (let i = 0; i < allPdfBytes.length; i++) {
            const item = allPdfBytes[i];
            const fileName = `lstFrontendPdf_${i}.pdf`;
            zip.file(fileName, item.pdfBytes); // já comprimido
            formData.append(`analyseSampleIDs`, item.analyseSampleID);
            analyseSampleIds.push(item.analyseSampleID);
            if ((i + 1) % 3 === 0) await microYield();
        }

        const zipFile = await zip.generateAsync({
            type: 'blob',
            compression: 'DEFLATE',
            compressionOptions: { level: 3 } // 1–3 rápido; 6–9 pesado
        });
        formData.append('zipFiles', zipFile);

        setProgress(0);

        const config = {
            onUploadProgress: (pe) => {
                const { loaded, total } = pe;
                if (total) {
                    const pct = Math.round((loaded * 100) / total);
                    setProgress(pct);
                }
            },
            headers: { 'Content-Type': 'multipart/form-data' },
        };

        try {
            const result = await api.post('/Recommendation/GetPdf', formData, config);

            if (result?.data?.response) {
                const decodedPdfContent = atob(result.data.response.fileContents);
                const blob = new Blob(
                    [Uint8Array.from(decodedPdfContent, (c) => c.charCodeAt(0))],
                    { type: 'application/pdf' }
                );

                const currentDate = new Date();
                const formattedDate = currentDate.toISOString().replace(/[:.]/g, "-");

                const downloadLink = document.createElement('a');
                downloadLink.href = URL.createObjectURL(blob);
                downloadLink.download = `Relatorio_${formattedDate}.pdf`;
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);

                if (!isSingle) {
                    setMessage('4/4 - Atualizando os registros, aguarde...');
                    setLoading(true);
                    setProgress(0);
                }

                const resultStatus = await api.post('/AnalyseSample/updateStatusToEnd', analyseSampleIds);

                if (resultStatus?.data?.response) {
                    if (!isSingle) {
                        setMessage('Relatório(s) gerado(s) com sucesso. A Janela será fechada dentro de instantes...');
                        setProgress(0);
                        setProgress(25);
                        setProgress(50);
                        setProgress(75);
                        setProgress(100);
                        setTimeout(function () {
                            window.close();
                        }, 3000);
                    } else {
                        setLoading(false);
                        setProgress(100);
                    }
                } else {
                    setMessage('Houve um erro na geração do(s) relatório(s), por favor tente novamente.');
                    console.error('Houve um erro na geração do(s) relatório(s), por favor tente novamente.');
                    setLoading(false);
                }
            }
        } catch (error) {
            console.error('Erro durante o envio para o backend:', error);
            setLoading(false);
        }
    };

    /* ===========================
       Render helpers (inalterados visualmente)
       =========================== */

    const handleDateFormater = (paramDate) => {
        if (paramDate) {
            const _date = new Date(paramDate);
            const day = (_date.getDate()).toString().padStart(2, '0');
            const month = (_date.getMonth() + 1).toString().padStart(2, '0');
            const year = _date.getFullYear().toString();
            const formattedDate = `${day}/${month}/${year}`;
            return formattedDate;
        }
        return undefined;
    };

    const renderInterpretationRowComplete = (description, intervalNormalValue, actualValue, valueDescription, thermometerValue, color, actualValue2, listParameterTemplateItem, roundingRule) => {
        return (
            <div className={styles.interpretationRow} style={{
                width: '169px', height: '140px', borderRadius: '8px', border: '1px solid #CCD1D6'
            }}>
                <div style={{ display: 'flex', padding: '2px', fontFamily: 'Montserrat' }}>
                    <div style={{ width: '70%', lineHeight: '10px' }}>
                        <div style={{ height: '35px' }}>
                            <p style={{ fontWeight: 'bold', color: '#ACB4BA', fontSize: '12px', lineHeight: '14px' }}>{description}</p>
                        </div>
                        <p style={{ fontWeight: 'bold', fontSize: '16px', padding: '8px 0 8px 0', lineHeight: 0.5 }}>
                            {actualValue2 ? actualValue2.toFixed((roundingRule != null ? roundingRule : 2)) : actualValue.toFixed((roundingRule != null ? roundingRule : 2))}
                        </p>
                        {listParameterTemplateItem !== null ? (
                            <p style={{ fontSize: '10px', fontWeight: 'bold' }}>Normal: <b style={{ fontSize: '9px' }}>{intervalNormalValue}</b></p>
                        ) : ''}
                        <p style={{ fontWeight: 'bold', fontSize: '10px', width: '100px' }}>
                            <div style={{ width: '12px', height: '12px', backgroundColor: `${color}`, borderRadius: '4px', position: 'absolute' }} />
                            <p style={{ paddingLeft: '16px', paddingTop: '2px' }}>{valueDescription}</p>
                        </p>
                    </div>

                    {listParameterTemplateItem !== null ? (
                        <div style={{ width: '30%', height: '125px', fontSize: '8px', fontWeight: 'bold', color: '#737D86', lineHeight: '10px' }}>
                            <div style={{
                                borderRadius: '100%', backgroundColor: 'white', border: '2px solid #0C8662', width: '19px', height: '19px',
                                position: 'absolute', marginLeft: '-5px', transform: `translateY(${570 - thermometerValue}%)`
                            }} />
                            {listParameterTemplateItem !== null ? listParameterTemplateItem.map(e => (
                                <p key={e.resultDescription} style={{ float: 'left', paddingLeft: '16px', width: '100%' }}>{e.resultDescription}</p>
                            )) : <></>}
                            {listParameterTemplateItem !== null ? (
                                <div style={{ background: linearGradient(listParameterTemplateItem), width: '8px', height: '100%', borderRadius: '5px' }} />
                            ) : <></>}
                        </div>
                    ) : <></>}
                </div>
            </div>
        );
    };

    const addCommas = (num) => NF_PTBR.format(num);

    const renderInterpretationRow = (description, actualValue, valueDescription, color, actualValue2, roundingRule) => {
        return (
            <div className={styles.interpretationRow} style={{ width: '105px', height: '85px', borderRadius: '8px', border: '1px solid #ACB4BA', marginTop: '-1%' }}>
                <div style={{ display: 'flex', padding: '2px', fontFamily: 'Montserrat' }}>
                    <div style={{ width: '70%', lineHeight: '10px' }}>
                        <div style={{ height: '35px' }}>
                            <p style={{ fontWeight: 'bold', fontSize: '12px', lineHeight: '14px' }}>{description}</p>
                        </div>
                        <p style={{ fontWeight: 'bold', fontSize: '16px', marginTop: "4px" }}>
                            {actualValue2 ? actualValue2.toFixed((roundingRule != null ? roundingRule : 1)) : actualValue.toFixed((roundingRule != null ? roundingRule : 1))}
                        </p>
                        <p style={{ fontWeight: 'bold', fontSize: '10px', width: '95px' }}>
                            <div style={{ width: '12px', height: '12px', backgroundColor: `${color}`, borderRadius: '4px', position: 'absolute' }} />
                            <p style={{ textAlign: 'center', paddingTop: '2px' }}>{valueDescription}</p>
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    const linearGradient = (listParameterTemplateItem) => {
        let text = 'linear-gradient(to bottom';
        listParameterTemplateItem.forEach(e => { text += ', ' + e.resultColor; });
        text += ')';
        return text;
    };

    const handleObservationList = (dataList) => {
        return dataList
            ? dataList.filter(e => e.description !== '')
                .map((e, index) => (
                    <Box key={index} style={{
                        width: '100%',
                        marginLeft: '2%',
                        marginTop: index === 0 ? '5px' : '0',
                        fontSize: '12px'
                    }}>
                        {e.description}
                    </Box>
                ))
            : null;
    };

    // Conteúdo do PDF (layout preservado 1:1)
    const PdfContent = () => {
        const combinedList = combinedListRef.current;
        const reportTemplate = reportTemplateRef.current;

        return (
            <div style={{ overflowX: 'hidden' }}>
                {combinedList ? combinedList.map((itemList, index) => {
                    const pdfDataId = `pdfData${itemList.analyseSampleReturn.analyseSampleID + itemList.analyseSampleParameterTemplates.parameterTemplateID}`;

                    return (
                        <div style={{ width: "794px", margin: '0 auto' }} key={pdfDataId}>
                            <div id={pdfDataId} style={{ width: "794px", margin: '0 auto', backgroundColor: 'white', borderRadius: '5px' }}>
                                <Box style={{ display: "flex", flexDirection: "column", padding: '2%', margin: '1%' }}>
                                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} >
                                        {itemList.analyseSampleParameterTemplates.parameterTemplate.tenant && itemList.analyseSampleParameterTemplates.parameterTemplate.tenant.logo ?
                                            <div style={{ maxWidth: "100px", maxHeight: '70px', borderRadius: '5px' }}>
                                                <img src={`data:image/jpeg;base64, ${itemList.analyseSampleParameterTemplates.parameterTemplate.tenant.logo}`} alt="Logo"
                                                    style={{ margin: '0px auto', width: '70%' }} />
                                            </div> : ''
                                        }
                                        <label style={{ fontFamily: "Montserrat", marginLeft: "1px", fontWeight: 700, fontSize: "20px", lineHeight: '41.45px', color: "black" }}>
                                            {reportTemplate ? reportTemplate.title : 'Recomendações de Produtividade do Solo'}
                                        </label>
                                    </Box>
                                    <Box style={{ display: "flex", width: "100%", marginTop: "-5px" }} >
                                        <div className={styles.border} />
                                    </Box>
                                    <Box className={styles.containerColumns} style={{ marginTop: '10px' }}>
                                        <div className={styles.columnsRow} style={{ width: '25%' }}>
                                            <p style={{ fontSize: '14px', fontWeight: 'bold' }}>Produtor</p>
                                            <p style={{ lineHeight: '12px' }}>
                                                {(itemList.analyseSampleReturn?.farmerCultureRegion?.externalCode ?? '')}
                                                {itemList.analyseSampleReturn?.farmerCultureRegion?.farmer?.name ? ` - ${itemList.analyseSampleReturn?.farmerCultureRegion?.farmer?.name}` : ''}
                                            </p>
                                        </div>
                                        {itemList.analyseSampleReturn?.instructor ?
                                            <div className={styles.columnsRow} style={{ width: '25%' }}>
                                                <p style={{ fontSize: '14px', fontWeight: 'bold' }}>Instrutor</p>
                                                <p style={{ lineHeight: '12px' }}>
                                                    {(itemList.analyseSampleReturn?.instructor?.externalCode ?? '')}
                                                    {itemList.analyseSampleReturn?.instructor?.name ? ` - ${itemList.analyseSampleReturn?.instructor?.name}` : ''}
                                                </p>
                                            </div> : ''
                                        }
                                        <div className={styles.columnsRow} style={{ width: '18%' }}>
                                            <p style={{ fontSize: '14px', fontWeight: 'bold' }}>Data Emissão</p>
                                            <p style={{ lineHeight: '12px' }}>{(itemList.analyseSampleReturn?.formattedDateEmission ? handleDateFormater(itemList.analyseSampleReturn?.dateEmission) : '')}</p>
                                        </div>
                                        <div className={styles.columnsRow} style={{ width: '10%' }}>
                                            <p style={{ fontSize: '14px', fontWeight: 'bold' }}>Cultura</p>
                                            <p style={{ lineHeight: '12px', marginTop: '-2%' }}>{(itemList.analyseSampleParameterTemplates.parameterTemplate.culture?.name ?? "Todas")}</p>
                                        </div>
                                        <div className={styles.columnsRow} style={{ width: '22%' }}>
                                            <p style={{ fontSize: '14px', fontWeight: 'bold' }}>Amostra</p>
                                            <p style={{ lineHeight: '12px', marginTop: '-2%' }}>{(itemList.analyseSampleReturn?.friendlyCode ?? '')}</p>
                                        </div>
                                    </Box>
                                    <Box style={{ display: "flex", width: "100%", marginTop: "-5px" }} >
                                        <div className={styles.border} />
                                    </Box>
                                    <Box style={{ width: "100%", paddingTop: '0px' }} >
                                        <p style={{ fontFamily: "Montserrat", marginLeft: "1%", fontWeight: 700, fontSize: "16px", marginTop: '5px' }}>Interpretação</p>
                                    </Box>
                                    <Box className={styles.interpretationElement} style={{
                                        height: (reportTemplate && reportTemplate.versionComplete == 0) ? '250px' : '840px',
                                    }}>
                                        <Box className={styles.interpretationElement} >
                                            {itemList.lstAnalyseSampleResultInterpretation ? itemList.lstAnalyseSampleResultInterpretation.map(e => {
                                                return (
                                                    <React.Fragment key={(e?.analyseSampleResult?.parameter?.id ?? '') + (e?.parameterTemplateItem?.parameterTemplateItemHeader?.id ?? '')}>
                                                        {reportTemplate && reportTemplate.versionComplete == 1 ?
                                                            renderInterpretationRowComplete(
                                                                e.parameterTemplateItem != null ? e.parameterTemplateItem.parameterTemplateItemHeader.description : e.analyseSampleResult.parameter.description
                                                                , e.intervalNormalValue
                                                                , e.analyseSampleResult.value
                                                                , e.parameterTemplateItem ? e.parameterTemplateItem.resultDescription : ""
                                                                , e.thermometerValue
                                                                , e.parameterTemplateItem ? e?.parameterTemplateItem.resultColor : 'white'
                                                                , e?.analyseSampleResultDependency?.value ?? null
                                                                , e.listParameterTemplateItemDto ? e.listParameterTemplateItemDto : null
                                                                , e.analyseSampleResultDependency != null ? e.analyseSampleResultDependency.parameter.roundingRule : e.analyseSampleResult.parameter.roundingRule)
                                                            :
                                                            renderInterpretationRow(
                                                                e.parameterTemplateItem != null ? e.parameterTemplateItem.parameterTemplateItemHeader.description : e.analyseSampleResult.parameter.description
                                                                , e.analyseSampleResult.value
                                                                , e.parameterTemplateItem ? e.parameterTemplateItem.resultDescription : ""
                                                                , e.parameterTemplateItem ? e?.parameterTemplateItem.resultColor : 'white'
                                                                , e?.analyseSampleResultDependency?.value ?? null
                                                                , e.analyseSampleResultDependency != null ? e.analyseSampleResultDependency.parameter.roundingRule : e.analyseSampleResult.parameter.roundingRule)
                                                        }
                                                    </React.Fragment>
                                                )
                                            }) : ''}
                                        </Box>
                                    </Box>

                                    <Box style={{ display: "flex", width: "100%", paddingTop: '10px' }} >
                                        <div className={styles.border} />
                                    </Box>

                                    {(reportTemplate && reportTemplate.hasRecommendation) || reportTemplate == null ?
                                        <Box>
                                            <Box style={{
                                                height: itemList.lstParameterTemplateItem && itemList.lstParameterTemplateItem.every(e => !e.parameterTemplateItemHeader.isReportRecommendation) ? '130px' : ((reportTemplate && reportTemplate.versionComplete == 0) ? 'auto' : '850px'),
                                                marginTop: "2%"
                                            }}>
                                                {itemList.lstParameterTemplateItem ? itemList.lstParameterTemplateItem.map(e => {
                                                    return (
                                                        e.parameterTemplateItemHeader.isReportRecommendation ?
                                                            <React.Fragment key={e.parameterTemplateItemID}>
                                                                {e.parameterTemplateItemID !== null && e.listParameterTemplateItemRecommendationDto.length > 0 ?
                                                                    e.listParameterTemplateItemRecommendationDto.map((p, idx) => {
                                                                        return (
                                                                            <Grid item xs={12} style={{ marginTop: "-1%", fontSize: '12px' }} key={idx}>
                                                                                <Card style={{ backgroundColor: "white", border: "1px solid #F2F5F8", height: "auto", padding: '6px' }}>
                                                                                    <Box style={{ display: "column" }}>
                                                                                        <Grid style={{ display: "flex", marginLeft: "1%", marginTop: "1%" }} alignItems="flex-start" container spacing={1}>
                                                                                            <Grid item xs={12}>
                                                                                                <label style={{ fontWeight: 600, }} >{p.title}</label>
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                        <Grid style={{ display: "flex", marginLeft: "1%" }} alignItems="flex-start" container spacing={1}>
                                                                                            <Grid item xs={2}>
                                                                                                <label style={{ fontWeight: 600 }}>Corretivo do Solo</label>
                                                                                            </Grid>
                                                                                            <Grid item xs={2}>
                                                                                                <label style={{ fontWeight: 600 }}>Quantidade</label>
                                                                                            </Grid>
                                                                                            <Grid item xs={8}>
                                                                                                <label style={{ fontWeight: 600 }}>Instrução de dosagem</label>
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                        <Grid style={{ display: "flex", marginLeft: "1%", marginRight: "1%" }} alignItems="flex-start" container spacing={1}>
                                                                                            <Grid item xs={2} style={{ marginTop: "-1%" }}>
                                                                                                <label>{p.recommendation?.description}</label>
                                                                                            </Grid>
                                                                                            <Grid item xs={2} style={{ marginTop: "-1%" }}>
                                                                                                <label>
                                                                                                    {`${addCommas(p.amount)} ${p.recommendation.unitOfMeasure.description} ${p.recommendation.type}`}</label>
                                                                                            </Grid>
                                                                                            <Grid item xs={8} style={{ marginTop: "-1%" }}>
                                                                                                <label>{p.instruction}</label>
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                        <Grid style={{ display: "flex", marginLeft: "1%" }} alignItems="flex-start" container spacing={1}>
                                                                                            <Grid item xs={12}>
                                                                                                <label style={{ fontWeight: 600 }}>Observação</label>
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                        <Grid style={{ display: "flex", marginLeft: "1%", marginRight: "1%" }} alignItems="flex-start" container spacing={1}>
                                                                                            <Grid item xs={12} style={{ marginTop: "-1%" }}>
                                                                                                <label >{p.observation}</label>
                                                                                            </Grid>
                                                                                        </Grid>
                                                                                    </Box>
                                                                                </Card>
                                                                            </Grid>
                                                                        )
                                                                    })
                                                                    : ''
                                                                }
                                                            </React.Fragment>
                                                            :
                                                            ''
                                                    )
                                                }) : ''}
                                                {handleObservationList(itemList.observationList)}
                                            </Box >
                                            <Box style={{ display: "flex", width: "100%", paddingTop: '20px', marginTop: '-18px' }} >
                                                <div className={styles.border} />
                                            </Box>
                                        </Box>
                                        : ''
                                    }

                                    <Box style={{
                                        marginLeft: '1%',
                                        height: (reportTemplate && reportTemplate.versionComplete == 0) ? 'auto' : '850px'
                                    }}>
                                        {itemList.lstAnalyseSampleResultInterpretation ? itemList.lstAnalyseSampleResultInterpretation.map((e, idx2) => {
                                            return (
                                                <Box className={styles.interpretationElement} key={idx2}>
                                                    {itemList.analyseSampleParameterTemplates.parameterTemplate.summaryNPK == true ?
                                                        e.listRecommendationResultDto !== undefined && e.listRecommendationResultDto.length > 0 ?
                                                            <Grid alignItems="flex-start" style={{ marginTop: '5px', marginBottom: '5px !important', width: "100%" }} container spacing={0.5}>
                                                                <Grid >
                                                                    <label className={styles.tableLabelsHeader}>{reportTemplate && reportTemplate.titleNPK !== null ? reportTemplate.titleNPK : ''}</label>
                                                                </Grid>
                                                                {e.recommendationRequestDto ?
                                                                    <Grid item xs={2} style={{ marginTop: index > 0 && index % 2 === 0 ? "5px" : "-1%", fontSize: '12px' }}>
                                                                        <Card style={{ backgroundColor: "white", border: "1px solid #F2F5F8", height: "auto", padding: '6px' }}>
                                                                            <Box style={{ display: "column" }}>
                                                                                <Grid style={{ display: "flex", marginLeft: "1%", marginTop: "1%" }} alignItems="flex-start" container spacing={1}>
                                                                                    <Grid item xs={12}>
                                                                                        <label style={{ fontWeight: 600 }}>{'Kg/ha'}</label>
                                                                                    </Grid>
                                                                                </Grid>
                                                                                <Grid style={{ display: "flex", marginLeft: "1%" }} alignItems="flex-start" container spacing={1}>
                                                                                    <Grid item xs={4}><label>N</label></Grid>
                                                                                    <Grid item xs={4}><label>P</label></Grid>
                                                                                    <Grid item xs={4}><label>K</label></Grid>
                                                                                </Grid>
                                                                                <Grid style={{ display: "flex", marginLeft: "1%", marginRight: "1%" }} alignItems="flex-start" container spacing={1}>
                                                                                    <Grid item xs={4} style={{ marginTop: "-1%" }}><label style={{ fontWeight: 600 }}>{e.recommendationRequestDto.n}</label></Grid>
                                                                                    <Grid item xs={4} style={{ marginTop: "-1%" }}><label style={{ fontWeight: 600 }}>{e.recommendationRequestDto.p}</label></Grid>
                                                                                    <Grid item xs={4} style={{ marginTop: "-1%" }}><label style={{ fontWeight: 600 }}>{e.recommendationRequestDto.k}</label></Grid>
                                                                                </Grid>
                                                                            </Box>
                                                                        </Card>
                                                                    </Grid> : <></>}
                                                            </Grid> : <></>
                                                        :
                                                        e.listRecommendationResultDto !== undefined && e.listRecommendationResultDto.length > 0 ?
                                                            <Grid alignItems="flex-start" style={{ marginTop: '10px', marginRight: "15px !important", marginBottom: '5px !important', width: "100%" }} container spacing={0.5}>
                                                                <Grid >
                                                                    <label className={styles.tableLabelsHeader}>{reportTemplate && reportTemplate.titleNPK !== null ? reportTemplate.titleNPK : ''}</label>
                                                                </Grid>
                                                                {e.listRecommendationResultDto.map((p, index) => {
                                                                    return (
                                                                        <Grid item xs={6} style={{ marginTop: index > 0 && index % 2 === 0 ? "5px" : "-1%", fontSize: '12px' }} key={index}>
                                                                            <Card style={{ backgroundColor: "white", border: "1px solid #F2F5F8", height: "auto", padding: '6px' }}>
                                                                                <Box style={{ display: "column" }}>
                                                                                    <Grid style={{ display: "flex", marginLeft: "5px", marginTop: "0" }} alignItems="flex-start" container spacing={1}>
                                                                                        <Grid item xs={12}>
                                                                                            <label style={{ fontWeight: 600 }}>{p.fertilizerCompositionStage.stage.description}</label>
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                    <Grid style={{ display: "flex", marginLeft: "5px" }} alignItems="flex-start" container spacing={1}>
                                                                                        <Grid item xs={2}><label>Kg/ha</label></Grid>
                                                                                        <Grid item xs={2}><label>N</label></Grid>
                                                                                        <Grid item xs={2}><label>P2O5</label></Grid>
                                                                                        <Grid item xs={2}><label>K2O</label></Grid>
                                                                                        <Grid item xs={4}><label>Fórmula Sugerida</label></Grid>
                                                                                    </Grid>
                                                                                    <Grid style={{ display: "flex", marginLeft: "1%", marginRight: "5px" }} alignItems="flex-start" container spacing={1}>
                                                                                        <Grid item xs={2} style={{ marginTop: "-1%" }}><label style={{ fontWeight: 600 }}>{p.total}</label></Grid>
                                                                                        <Grid item xs={2} style={{ marginTop: "-1%" }}><label style={{ fontWeight: 600 }}>{p.total_N.toFixed(0)}</label></Grid>
                                                                                        <Grid item xs={2} style={{ marginTop: "-1%" }}><label style={{ fontWeight: 600 }}>{p.total_P.toFixed(0)}</label></Grid>
                                                                                        <Grid item xs={2} style={{ marginTop: "-1%" }}><label style={{ fontWeight: 600 }}>{p.total_K.toFixed(0)}</label></Grid>
                                                                                        <Grid item xs={4} style={{ marginTop: "-1%" }}><label style={{ fontWeight: 600 }}>{p.fertilizerCompositionStage.fertilizerComposition.description}</label></Grid>
                                                                                    </Grid>
                                                                                </Box>
                                                                            </Card>
                                                                        </Grid>
                                                                    )
                                                                })}
                                                            </Grid> : <></>
                                                    }
                                                </Box>
                                            )
                                        }) : ''
                                        }
                                    </Box>

                                    <Box style={{ marginTop: "5px", display: "flex", width: "100%" }}>
                                        <label style={{ fontFamily: "Montserrat", fontSize: 12, color: "#161C24", margin: "0 auto" }} >Responsável Técnico</label>
                                    </Box>
                                    <Box style={{ display: "flex", width: "50%", margin: "0 auto", marginTop: "30px" }} >
                                        <div className={styles.border} />
                                    </Box>
                                    <Box style={{ display: "column", width: "100%" }}>
                                        <Box style={{ display: "flex" }}>
                                            <label style={{ fontFamily: "Montserrat", fontSize: 16, color: "#161C24", margin: "0 auto" }}>{(itemList.analyseSampleReturn && itemList.analyseSampleReturn.agronomist ? itemList.analyseSampleReturn.agronomist.name : "")}</label>
                                        </Box>
                                        <Box style={{ display: "flex" }}>
                                            <label style={{ fontFamily: "Montserrat", fontSize: 12, marginLeft: '10px', color: "#161C24", margin: "0 auto" }}>{itemList.agronomist ? (itemList.agronomist.name) : 'Agrônomo Selecionado'}</label>
                                        </Box>
                                        <Box style={{ display: "flex" }}>
                                            <label style={{ fontFamily: "Montserrat", fontSize: 12, marginLeft: '10px', color: "#161C24", margin: "0 auto" }}>{itemList.agronomist ? (itemList.agronomist.descriptionFunction + " " + itemList.agronomist.crea) : ''}</label>
                                        </Box>
                                    </Box>
                                    <Box style={{ display: "flex", width: "100%" }} >
                                        <div className={styles.border} />
                                    </Box>
                                    <Box style={{ display: "flex", width: "100%", paddingTop: '5px' }}>
                                        <Box style={{ display: "flex", width: "65%", marginLeft: "1%" }}>
                                            <p style={{ fontSize: 7, color: "#000000", margin: "0 auto", fontFamily: 'Montserrat', lineHeight: '15px' }}>
                                                As interpretações e recomendações apresentadas neste documento são aplicáveis somente a(s) área(s) da(s) amostra(s) analisada(s) conforme laudo de análise de solo emitido por laboratório credenciado a Rede Oficial de Laboratório de Análise de Solos (ROLAS). Os tipos, fórmulas e quantidades de fertilizantes acima indicados são informações referenciais, podendo a parte interessada utilizar fórmulas e dosagens similares disponíveis no mercado, desde que amparada por nova Recomendação para adequação da fertilização.
                                            </p>
                                        </Box>
                                        <Box style={{ display: "flex", width: "35%", marginTop: "3%", textAlign: 'right' }}>
                                            <div style={{ float: 'right', margin: "0 0 0 auto" }}>
                                                <img src={data_results_logo} alt="logo" style={{ margin: "0 auto", maxWidth: '200px', height: 'auto', alignItems: 'right' }} />
                                            </div>
                                        </Box>
                                    </Box>
                                </Box>
                                {isLoadingEvents ? <LoadingEvents /> : <></>}
                            </div>

                            {itemList.analyseSampleReturn.securityCode != null ?
                                <Box style={{ paddingTop: '20px' }}>
                                    <iframe
                                        title="Relatório Autêntico"
                                        src={'https://online.unisc.br/centralAnalitica/relatoriosUnisc/relatorioautentico.php?codigo=' + itemList.analyseSampleReturn.securityCode + '&lab=1'}
                                        width="100%"
                                        height="500px"
                                        frameBorder="0"
                                        allowFullScreen
                                        data-skip-capture // <— evita capturar o iframe no canvas
                                    />
                                </Box>
                                :
                                <Box style={{ padding: '20px', textAlign: 'center', fontWeight: 'bold', border: '1px solid black' }}>
                                    Não foi possível encontrar o Laudo do Laboratório.
                                </Box>
                            }
                        </div>)
                }) : <></>}
            </div >
        );
    };

    const clickToList = async () => {
        history.push({ pathname: "/analyseSample/" })
    };

    if (loading) {
        return (
            <Alert severity="info" sx={{ fontSize: '1.5rem' }}>
                {message}
                <LinearProgress variant="determinate" value={progress} sx={{ width: '100%', marginTop: 2, height: '0.5rem' }} />
            </Alert>
        )
    }

    const isDownloadReport = isDownloadReportRef.current;

    return (
        <div style={{ overflowX: 'hidden' }}>
            <div style={{ display: 'flex', margin: '0 auto', width: "794px" }}>
                <Box className={styles.block1} style={{ margin: '0 auto', width: "50%", paddingLeft: 0, paddingTop: '6px' }} >
                    <button className='btn' onClick={() => { history.goBack(); }}>
                        <img src={ArrowLeft} className='icon' alt="Voltar" />
                    </button>
                    <label style={{ fontFamily: "Montserrat", marginLeft: 20, fontWeight: 700, fontSize: 22, paddingTop: '17px', color: "#161C24" }} >Análises</label>
                </Box>
                <div style={{ width: "50%", margin: '0 auto', padding: '20px 0 20px 0', textAlign: 'right' }}>
                    <Button className={styles.button} onClick={clickToList} style={{ marginRight: '10px' }}>
                        <ImUndo2 size={15} />
                        <label style={{ height: "auto", cursor: "pointer", paddingLeft: '6px' }}>Ir para listagem</label>
                    </Button>
                    <Button className={styles.button} onClick={generateAndDownloadPdf}>
                        <img src={download} style={{ width: '8%' }} className='icon' alt="Baixar" />
                        <label style={{ height: "auto", cursor: "pointer", paddingLeft: '6px' }}>Salvar PDF</label>
                    </Button>
                </div>
            </div>
            {isDownloadReport ? '' : <PdfContent />}
        </div >
    );
};

export default RecomendationReport;
